import {Injectable} from '@angular/core';
import {
  GA4EventConfig,
  GA4ProductConfig,
  GoogleAnalyticsEventsService
} from '../../shared/services/google-analytics-events.service';
import {GA4EventKey} from '../consts/ga4-event-key.enum';
import {ApplicationStateService} from '../../shared/services/application-state.service';
import {Ga4EventName} from '../consts/ga4-event-name.enum';
import {Ga4EventPayload} from '../models/ga4-event-payload';
import {WizardService} from './wizard.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {PRODUCT_CONFIG} from '../configs/ga4-events/product-config';
import {Ga4OffersElementKey} from '../consts/ga4-offers-element-key.enum';
import {TranslateService} from '@ngx-translate/core';
import {Ga4CustomEventsHelper} from '../utils/ga4-custom-events-helper';
import {PaymentMethod} from '../consts/payment-method.enum';

@Injectable({
  providedIn: 'root'
})
export class Ga4EventsConfigService {

  private userIdSubmitted: boolean = false;

  get productConfig(): GA4ProductConfig {
    return PRODUCT_CONFIG;
  }

  get currentEventStepName(): string {
    const stepMapping = this.productConfig.stepsMapping.find(step => step.stepKey === this.wizardService.currentStepKey);
    return stepMapping ? stepMapping.eventStepName : null;
  }

  get tuName(): string {
    if (this.wizardService.isThirdStep || this.wizardService.isNotificationStep) {
      return this.stateService.insuranceCompanyCode;
    }
    return null;
  }

  get productVariant(): string {
    if (this.wizardService.isThirdStep || this.wizardService.isNotificationStep) {
      let offerPackageName;
      if (this.stateService.selectedOfferPackageName) {
        offerPackageName = this.stateService.selectedOfferPackageName;
      } else if (isNotNullOrUndefined(this.stateService.offerPackageNameBusinessParam)) {
        offerPackageName = this.stateService.offerPackageNameBusinessParam;
      }

      if (offerPackageName) {
        return `${this.productConfig.productName}_${offerPackageName}`;
      }
    }
    return null;
  }

  get shouldIncludeUserId(): boolean {
    return !this.userIdSubmitted && isNotNullOrUndefined(this.stateService.analyticsUserId);
  }

  get userIdToInclude(): string | null {
    return this.shouldIncludeUserId ? this.stateService.analyticsUserId : null;
  }

  constructor(private googleAnalyticsService: GoogleAnalyticsEventsService,
              private stateService: ApplicationStateService,
              private ts: TranslateService,
              private wizardService: WizardService) { }

  init() {
    this.handleEventSubmitted();
    this.googleAnalyticsService.init(this.buildConfig());
    if (this.stateService.firstEntry) {
      this.googleAnalyticsService.send(GA4EventKey.INIT);
    }
    this.googleAnalyticsService.send(GA4EventKey.FIELD_CLICK_ENTER);
    this.handleCustomEvents();
  }

  sendOffersElementEvent(elementKey: Ga4OffersElementKey): void {
    const elementName = this.ts.instant(`GA4_EVENTS.OFFERS_ELEMENT.${elementKey}`);
    this.googleAnalyticsService.send(GA4EventKey.OFFERS_ELEMENT_CLICK, {
      offer_element_name: elementName
    });
  }

  sendPaymentInitEvent(paymentMethod: PaymentMethod): void {
    const paymentMethodConfig = this.productConfig.paymentMethods.find(config => config.method === paymentMethod);
    if (isNotNullOrUndefined(paymentMethodConfig)) {
      const paymentType = this.ts.instant(`GA4_EVENTS.PAYMENT_TYPE.${paymentMethodConfig.eventPaymentType}`);
      this.googleAnalyticsService.send(GA4EventKey.PAYMENT_INIT, {
        event: paymentMethodConfig.eventName,
        payment_type: paymentType
      });
    }
  }

  sendRecalculationEvent(paymentMethod: PaymentMethod): void {
    let paymentType;
    const paymentMethodConfig = this.productConfig.paymentMethods.find(config => config.method === paymentMethod);
    if (isNotNullOrUndefined(paymentMethodConfig)) {
      paymentType = this.ts.instant(`GA4_EVENTS.PAYMENT_TYPE.${paymentMethodConfig.eventPaymentType}`);
    }
    this.googleAnalyticsService.send(GA4EventKey.RECALCULATION, {
      payment_type: paymentType ? paymentType : null
    });
  }

  private buildConfig(): GA4EventConfig[] {
    return [
      {
        key: GA4EventKey.INIT,
        eventName: Ga4EventName.CALCULATOR_START,
        buildPayload: () => ({
          product: this.productConfig.productName,
          user_id: this.userIdToInclude
        })
      },
      {
        key: GA4EventKey.FIELD_CLICK,
        eventName: Ga4EventName.CALCULATOR_FIELD_CLICK,
        buildPayload: (partialPayload?: Ga4EventPayload) => ({
          product: this.productConfig.productName,
          calculator_step: this.currentEventStepName,
          tu_name: this.tuName,
          product_variant: this.productVariant,
          calculator_field_name: partialPayload.calculator_field_name,
          calculator_field_value: isNotNullOrUndefined(partialPayload.calculator_field_value) ? partialPayload.calculator_field_value : undefined,
          user_id: this.userIdToInclude
        })
      },
      {
        key: GA4EventKey.FIELD_CLICK_ENTER,
        eventName: Ga4EventName.CALCULATOR_FIELD_CLICK,
        buildPayload: () => ({
          product: this.productConfig.productName,
          calculator_step: this.currentEventStepName,
          tu_name: this.tuName,
          product_variant: this.productVariant,
          calculator_field_name: 'enter',
          user_id: this.userIdToInclude
        })
      },
      {
        key: GA4EventKey.FIELD_VALUE_DELETE,
        eventName: Ga4EventName.CALCULATOR_FIELD_DELETE,
        buildPayload: (partialPayload?: Ga4EventPayload) => ({
          product: this.productConfig.productName,
          calculator_step: this.currentEventStepName,
          tu_name: this.tuName,
          product_variant: this.productVariant,
          calculator_field_name_deleted: partialPayload.calculator_field_name_deleted,
          calculator_field_value_deleted: partialPayload.calculator_field_value_deleted
        })
      },
      {
        key: GA4EventKey.FIELD_ERROR,
        eventName: Ga4EventName.CALCULATOR_FIELD_ERROR,
        buildPayload: (partialPayload?: Ga4EventPayload) => ({
          product: this.productConfig.productName,
          calculator_step: this.currentEventStepName,
          tu_name: this.tuName,
          product_variant: this.productVariant,
          calculator_field_name: partialPayload.calculator_field_name,
          calculator_error_message: partialPayload.calculator_error_message,
          user_id: this.userIdToInclude
        })
      },
      {
        key: GA4EventKey.OFFERS_ELEMENT_CLICK,
        eventName: Ga4EventName.OFFER_ELEMENT_CLICK,
        buildPayload: (partialPayload?: Ga4EventPayload) => ({
          product: this.productConfig.productName,
          offer_element_name: partialPayload.offer_element_name,
          user_id: this.userIdToInclude
        })
      },
      {
        key: GA4EventKey.OFFER_FILTER_CLICK,
        eventName: Ga4EventName.OFFER_FILTER_CLICK,
        buildPayload: (partialPayload?: Ga4EventPayload) => ({
          product: this.productConfig.productName,
          offer_filter_name: partialPayload.offer_filter_name,
          offer_filter_option: partialPayload.offer_filter_option,
          user_id: this.userIdToInclude
        })
      },
      {
        key: GA4EventKey.PAYMENT_STEP_ELEMENT_CLICK,
        eventName: Ga4EventName.CHECKOUT_ELEMENT_CLICK,
        buildPayload: (partialPayload?: Ga4EventPayload) => ({
          product: this.productConfig.productName,
          tu_name: this.tuName,
          product_variant: this.productVariant,
          checkout_section_name: partialPayload.checkout_section_name,
          checkout_element_name: partialPayload.checkout_element_name,
          user_id: this.userIdToInclude
        })
      },
      {
        key: GA4EventKey.STEP_INIT,
        eventName: Ga4EventName.CALCULATOR_VIEW,
        buildPayload: () => ({
          product: this.productConfig.productName,
          calculator_step: this.currentEventStepName,
          tu_name: this.tuName,
          product_variant: this.productVariant,
          user_id: this.userIdToInclude
        }),
      },
      {
        key: GA4EventKey.PAYMENT_INIT,
        buildPayload: (partialPayload?: Ga4EventPayload) => ({
          event: partialPayload.event,
          product: this.productConfig.productName,
          tu_name: this.tuName,
          product_variant: this.productVariant,
          payment_type: partialPayload.payment_type,
          payment_funnel: 'true',
          user_id: this.userIdToInclude
        }),
      },
      {
        key: GA4EventKey.RECALCULATION,
        eventName: Ga4EventName.RECALCULATION_VIEW,
        buildPayload: (partialPayload?: Ga4EventPayload) => ({
          product: this.productConfig.productName,
          tu_name: this.tuName,
          product_variant: this.productVariant,
          payment_type: partialPayload ? partialPayload.payment_type : null,
          payment_funnel: 'true',
          user_id: this.userIdToInclude
        }),
      },
    ];
  }

  private handleCustomEvents(): void {
    Ga4CustomEventsHelper.buyOnlineFromPresentationEvent.subscribe(() => this.sendOffersElementEvent(Ga4OffersElementKey.BUY_ONLINE_MODAL));
    Ga4CustomEventsHelper.sortOffersSelectClickEvent.subscribe(() => this.sendOffersElementEvent(Ga4OffersElementKey.SORT_OFFERS));
    Ga4CustomEventsHelper.paymentInitEvent.subscribe(paymentMethod => this.sendPaymentInitEvent(paymentMethod));
    Ga4CustomEventsHelper.recalculationEvent.subscribe(paymentMethod => this.sendRecalculationEvent(paymentMethod));
  }

  private handleEventSubmitted(): void {
    this.googleAnalyticsService.newEventSubmitted$.subscribe(eventData => {
      const { payload } = eventData;
      if (payload && !!payload.user_id) {
        this.userIdSubmitted = true;
      }
    });
  }
}
